import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  DataTable,
  StatusBadge,
  Button,
  Input,
  Select
} from '@besiktning/ui'
import { Search, Eye, FileImage, List } from 'lucide-react'
import { garageApi, formatDateTime, formatCurrency } from '@besiktning/ui'
import type { Quote } from '@besiktning/ui'
import { t } from '../i18n'

export default function Quotes() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const { data: quotes = [], isLoading, error } = useQuery({
    queryKey: ['quotes', statusFilter],
    queryFn: () => garageApi.getQuotes({ status: statusFilter || undefined }),
  })

  // Ensure quotes is an array
  const quotesArray = Array.isArray(quotes) ? quotes : []

  const filteredQuotes = quotesArray.filter((quote: Quote) => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      quote.inspection_request?.vehicle?.reg_number?.toLowerCase().includes(searchLower) ||
      quote.inspection_request?.vehicle?.make?.toLowerCase().includes(searchLower)
    )
  })

  const columns = [
    {
      key: 'vehicle',
      header: t('quotes.colVehicle'),
      render: (quote: Quote & { inspection_request?: any }) => (
        <div>
          <p className="font-medium">
            {quote.inspection_request?.vehicle?.make} {quote.inspection_request?.vehicle?.model}
          </p>
          <p className="text-sm text-gray-500">
            {quote.inspection_request?.vehicle?.reg_number}
            {quote.inspection_request?.has_inspection_report && (
              <span className="ml-2 inline-flex items-center text-blue-600" title={t('quotes.hasInspectionReport')}>
                <FileImage className="h-3.5 w-3.5" />
              </span>
            )}
          </p>
        </div>
      ),
    },
    {
      key: 'price',
      header: t('quotes.colPrice'),
      render: (quote: Quote & { line_items?: any[] }) => (
        <div className="flex items-center gap-2">
          <div>
            <span className="font-semibold text-lg">
              {formatCurrency(quote.calculated_total || quote.price)}
            </span>
            <p className="text-xs text-gray-500">{t('quotes.inclVat')}</p>
          </div>
          {quote.line_items && quote.line_items.length > 0 && (
            <span className="inline-flex items-center text-green-600" title={t('quotes.hasWorkOrder')}>
              <List className="h-4 w-4" />
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('quotes.colStatus'),
      render: (quote: Quote) => (
        <StatusBadge status={quote.status} />
      ),
    },
    {
      key: 'sent',
      header: t('quotes.colSent'),
      render: (quote: Quote) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(quote.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (quote: Quote) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(`/requests/${quote.inspection_request_id}`, '_blank')}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ]

  // Calculate stats
  const totalQuotes = quotesArray.length
  const pendingQuotes = quotesArray.filter((q: Quote) => q.status === 'pending').length
  const acceptedQuotes = quotesArray.filter((q: Quote) => q.status === 'accepted').length
  const conversionRate = totalQuotes > 0 
    ? Math.round((acceptedQuotes / totalQuotes) * 100) 
    : 0

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('quotes.title')}</h1>
        <p className="text-gray-600">
          {t('quotes.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('quotes.statTotal')}</p>
            <p className="text-2xl font-bold">{totalQuotes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('quotes.statPending')}</p>
            <p className="text-2xl font-bold">{pendingQuotes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('quotes.statAccepted')}</p>
            <p className="text-2xl font-bold">{acceptedQuotes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('quotes.statConversion')}</p>
            <p className="text-2xl font-bold">{conversionRate}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <p className="font-medium">{t('quotes.errorHeading')}</p>
          <p className="text-sm">{error instanceof Error ? error.message : t('quotes.errorGeneric')}</p>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('quotes.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: '', label: t('quotes.statusAll') },
                  { value: 'pending', label: t('quotes.statusPending') },
                  { value: 'accepted', label: t('quotes.statusAccepted') },
                  { value: 'rejected', label: t('quotes.statusRejected') },
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('quotes.tableTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={filteredQuotes}
            keyExtractor={(quote) => quote.id}
            isLoading={isLoading}
            emptyMessage={t('quotes.emptyMessage')}
          />
          
          {/* Debug info - remove in production */}
          {!isLoading && quotesArray.length === 0 && !error && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
              <p className="font-medium">{t('quotes.emptyMessage')}</p>
              <p>{t('quotes.emptyDetail')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
